import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * Drawing tools overlay for the candle chart.
 *
 * Architecture
 * ────────────
 * - We DON'T render anything into the lightweight-chart canvas (would force
 *   a full chart redraw on every mouse move). Instead we render an absolutely
 *   positioned SVG layer on top of the chart container.
 * - Drawings are stored as logical (time, price) pairs — never raw pixels.
 *   On every pan / zoom / resize we re-translate them to pixel coords via
 *   `chart.timeScale().timeToCoordinate()` and `candleSeries.priceToCoordinate()`.
 * - When a tool is active we capture mouse events on this overlay so the
 *   chart doesn't pan. When `activeTool === null` the overlay sets
 *   `pointer-events: none` so the chart panning works normally — drawings
 *   stay visible but non-interactive (i.e. zero cost).
 *
 * Tools
 * ─────
 *   trendline  — 2 clicks → straight line
 *   ruler      — 2 clicks → distance label (Δprice, Δ%, bars)
 *   fib        — 2 clicks (low + high) → 7 horizontal levels
 */
const TOOL_COLORS = {
  trendline: "#facc15", // amber
  ruler: "#22d3ee",     // cyan
  fib: "#a78bfa",       // violet
};

export default function ChartDrawingOverlay({ chartRef, activeTool, drawings, onAddDrawing, onSetActiveTool, height, bars }) {
  const ovRef = useRef(null);
  const [hover, setHover] = useState(null);      // {x, y, time, price}
  const [pending, setPending] = useState(null);  // first click while drawing
  // version bumps to force re-projection on pan/zoom
  const [, setTick] = useState(0);

  // ───────────────────────────────────────────────────────────────
  // Subscribe to chart pan/zoom so we re-project all drawings.
  // We throttle with requestAnimationFrame so a long pan doesn't queue
  // up state updates — chart stays smooth even with many drawings.
  // ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const ctx = chartRef.current;
    if (!ctx?.chart) return;
    let raf = 0;
    const bump = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setTick((t) => (t + 1) % 1_000_000);
      });
    };
    const ts = ctx.chart.timeScale();
    ts.subscribeVisibleTimeRangeChange(bump);
    ts.subscribeVisibleLogicalRangeChange(bump);
    const onResize = () => bump();
    window.addEventListener("resize", onResize);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(bump);
      ts.unsubscribeVisibleLogicalRangeChange(bump);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [chartRef]);

  // Pixel → (time, price) using chart API.
  const pixelToTimePrice = useCallback((evt) => {
    const ctx = chartRef.current;
    if (!ctx?.chart || !ovRef.current) return null;
    const rect = ovRef.current.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const time = ctx.chart.timeScale().coordinateToTime(x);
    const price = ctx.candleSeries.coordinateToPrice(y);
    if (time == null || price == null) return null;
    return { x, y, time, price };
  }, [chartRef]);

  // (time, price) → pixel for rendering existing drawings.
  const proj = useCallback((time, price) => {
    const ctx = chartRef.current;
    if (!ctx?.chart) return null;
    const x = ctx.chart.timeScale().timeToCoordinate(time);
    const y = ctx.candleSeries.priceToCoordinate(price);
    if (x == null || y == null) return null;
    return { x, y };
  }, [chartRef]);

  // ───────────────────────────────────────────────────────────────
  // Mouse handlers — only active when a tool is selected.
  // ───────────────────────────────────────────────────────────────
  const onMouseMove = (e) => {
    if (!activeTool) return;
    const pt = pixelToTimePrice(e);
    if (pt) setHover(pt);
  };
  const onMouseLeave = () => setHover(null);
  const onClick = (e) => {
    if (!activeTool) return;
    const pt = pixelToTimePrice(e);
    if (!pt) return;
    if (!pending) {
      setPending(pt);
      return;
    }
    // Second click — finalise drawing
    onAddDrawing({
      id: Date.now(),
      kind: activeTool,
      a: { time: pending.time, price: pending.price },
      b: { time: pt.time, price: pt.price },
    });
    setPending(null);
    setHover(null);
    // Stay in tool mode so user can draw multiple shapes back-to-back.
  };
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setPending(null);
      onSetActiveTool(null);
    }
  }, [onSetActiveTool]);
  useEffect(() => {
    if (!activeTool) return;
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeTool, onKeyDown]);

  // Helpers for rendering --------------------------------------------------
  const renderTrendline = (d) => {
    const pa = proj(d.a.time, d.a.price);
    const pb = proj(d.b.time, d.b.price);
    if (!pa || !pb) return null;
    return (
      <line
        key={d.id}
        x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
        stroke={TOOL_COLORS.trendline} strokeWidth={1.5}
      />
    );
  };
  // TradingView-style measure box:
  //   • Translucent BLUE rectangle between the two clicks (always blue,
  //     direction encoded by the sign in the label).
  //   • Horizontal arrow inside the rect from start.x → end.x at the
  //     anchor-Y of the *destination* point (so it reads as "how much
  //     price moved over how many bars").
  //   • Vertical guide line at the destination edge.
  //   • Floating LABEL BOX above the chart showing 3 lines:
  //       +Δprice (+Δ%) ticks
  //       N bars, H h M m duration
  //       Vol X.XX K / M
  const renderRuler = (d) => {
    const pa = proj(d.a.time, d.a.price);
    const pb = proj(d.b.time, d.b.price);
    if (!pa || !pb) return null;
    const dPrice = d.b.price - d.a.price;
    const dPct = d.a.price ? (dPrice / d.a.price) * 100 : 0;
    const positive = dPrice >= 0;
    const sign = positive ? "+" : "";
    // Tick count — assume 0.01 tick (most NSE equities) and round.
    const ticks = Math.round(Math.abs(dPrice) / 0.01);
    // Bars + duration between the two anchors
    let nBars = 0;
    let volSum = 0;
    if (Array.isArray(bars)) {
      const lo = Math.min(d.a.time, d.b.time);
      const hi = Math.max(d.a.time, d.b.time);
      for (let i = 0; i < bars.length; i += 1) {
        if (bars[i].time >= lo && bars[i].time <= hi) {
          nBars += 1;
          volSum += bars[i].volume || 0;
        }
      }
    }
    const durSec = Math.abs(d.b.time - d.a.time);
    const durStr = (() => {
      if (durSec >= 86400) {
        const days = Math.floor(durSec / 86400);
        const h = Math.floor((durSec % 86400) / 3600);
        return h ? `${days}d ${h}h` : `${days}d`;
      }
      const h = Math.floor(durSec / 3600);
      const m = Math.floor((durSec % 3600) / 60);
      return `${h}h ${m}m`;
    })();
    const volStr = (() => {
      if (volSum >= 1e7) return `${(volSum / 1e7).toFixed(2)} Cr`;
      if (volSum >= 1e5) return `${(volSum / 1e5).toFixed(2)} L`;
      if (volSum >= 1e3) return `${(volSum / 1e3).toFixed(2)} K`;
      return String(volSum);
    })();
    // Geometry
    const x = Math.min(pa.x, pb.x);
    const y = Math.min(pa.y, pb.y);
    const w = Math.abs(pb.x - pa.x);
    const h = Math.abs(pb.y - pa.y);
    const BLUE = "#3b82f6";
    const arrowY = pb.y;
    // Label box — sized to fit the longest expected line; positioned
    // above the rect, centered horizontally but clamped to viewport.
    const labelW = 240;
    const labelH = 84;
    const labelX = Math.max(8, Math.min(x + w / 2 - labelW / 2, (ovRef.current?.clientWidth || 1200) - labelW - 8));
    const labelY = Math.max(8, y - labelH - 14);
    // Connecting tail from box to rect top
    const tailX = Math.min(Math.max(labelX + labelW / 2, x), x + w);
    return (
      <g key={d.id}>
        {/* Body rectangle */}
        <rect x={x} y={y} width={w} height={h} fill={BLUE} fillOpacity={0.15} stroke={BLUE} strokeWidth={1} strokeOpacity={0.55} />
        {/* Vertical guide lines at both edges */}
        <line x1={pa.x} y1={y} x2={pa.x} y2={y + h} stroke={BLUE} strokeWidth={1} strokeOpacity={0.7} />
        <line x1={pb.x} y1={y} x2={pb.x} y2={y + h} stroke={BLUE} strokeWidth={1} strokeOpacity={0.7} />
        {/* Horizontal arrow from start.x → end.x at the destination Y */}
        <line x1={pa.x} y1={arrowY} x2={pb.x - 6} y2={arrowY} stroke={BLUE} strokeWidth={1.5} />
        <polygon
          points={`${pb.x},${arrowY} ${pb.x - 8},${arrowY - 4} ${pb.x - 8},${arrowY + 4}`}
          fill={BLUE}
        />
        {/* Diagonal direction hint from pa → pb (subtle) */}
        <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={BLUE} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.45} />
        {/* Anchor markers */}
        <circle cx={pa.x} cy={pa.y} r={3} fill={BLUE} />
        <circle cx={pb.x} cy={pb.y} r={3} fill={BLUE} />
        {/* Connector from box to rect */}
        <line x1={tailX} y1={labelY + labelH} x2={tailX} y2={y} stroke={BLUE} strokeWidth={1} strokeOpacity={0.6} />
        {/* LABEL BOX — solid blue with bold white text */}
        <rect x={labelX} y={labelY} width={labelW} height={labelH} rx={6}
              fill={BLUE} stroke={BLUE} strokeWidth={1} />
        <text x={labelX + labelW / 2} y={labelY + 24}
              fill="#ffffff"
              style={{ fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace", fontSize: "13px", fontWeight: 700 }}
              textAnchor="middle">
          {`${sign}${dPrice.toFixed(2)} (${sign}${dPct.toFixed(2)}%) ${ticks.toLocaleString()}`}
        </text>
        <text x={labelX + labelW / 2} y={labelY + 47}
              fill="#ffffff"
              style={{ fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace", fontSize: "12px", fontWeight: 500, opacity: 0.95 }}
              textAnchor="middle">
          {`${nBars} bar${nBars !== 1 ? "s" : ""}, ${durStr}`}
        </text>
        <text x={labelX + labelW / 2} y={labelY + 68}
              fill="#ffffff"
              style={{ fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace", fontSize: "12px", fontWeight: 500, opacity: 0.95 }}
              textAnchor="middle">
          {`Vol ${volStr}`}
        </text>
      </g>
    );
  };
  // TradingView-style Fibonacci retracement:
  //   • 12 levels including deep extensions: 0, 0.236, 0.382, 0.5, 0.618,
  //     0.786, 1, 1.618, 2.618, 3.618, 4.236
  //   • Solid filled bands between each level (subtle alpha) → instantly
  //     readable retracement / extension zones
  //   • Labels on the LEFT of the first anchor, formatted as
  //     `ratio (price)` matching TradingView exactly
  //   • Two hollow anchor circles connected by a subtle dashed line
  //   • Bands extend full width of the visible chart (to the right edge)
  const renderFib = (d) => {
    const pa = proj(d.a.time, d.a.price);
    const pb = proj(d.b.time, d.b.price);
    if (!pa || !pb) return null;
    if (d.a.price === d.b.price) return null;
    // First click = 1.0 (100%), second click = 0 (0%) — matches TradingView
    // when user drags swing-high → swing-low. We compute prices by walking
    // outward from price1 toward price0 (and beyond for extensions).
    const p1 = d.a.price;
    const p0 = d.b.price;
    const range = p1 - p0; // signed
    const priceAt = (lvl) => p0 + range * lvl;
    // Bands cover the full chart width from first-click X to the right edge.
    const x0 = Math.min(pa.x, pb.x);
    const ovWidth = ovRef.current?.clientWidth || 1200;
    const xRight = Math.max(pa.x, pb.x);
    const xFar = ovWidth - 4;
    // Level palette closely matching TradingView's defaults.
    const LEVELS = [
      { lvl: 0,     color: "#787b86", bold: true,  fill: "#787b86" },
      { lvl: 0.236, color: "#f23645", bold: false, fill: "#f23645" },
      { lvl: 0.382, color: "#ff9800", bold: false, fill: "#ff9800" },
      { lvl: 0.5,   color: "#4caf50", bold: false, fill: "#4caf50" },
      { lvl: 0.618, color: "#089981", bold: true,  fill: "#089981" },
      { lvl: 0.786, color: "#00bcd4", bold: false, fill: "#00bcd4" },
      { lvl: 1,     color: "#787b86", bold: true,  fill: "#787b86" },
      { lvl: 1.618, color: "#2962ff", bold: true,  fill: "#2962ff" },
      { lvl: 2.618, color: "#f23645", bold: false, fill: "#f23645" },
      { lvl: 3.618, color: "#9c27b0", bold: false, fill: "#9c27b0" },
      { lvl: 4.236, color: "#e91e63", bold: true,  fill: "#e91e63" },
    ];
    const series = chartRef.current?.candleSeries;
    if (!series?.priceToCoordinate) return null;
    const rows = LEVELS.map((L) => {
      const price = priceAt(L.lvl);
      const y = series.priceToCoordinate(price);
      return y == null ? null : { ...L, price, y };
    }).filter(Boolean);
    if (rows.length < 2) return null;
    // Pre-format prices with comma thousands separator like TradingView.
    const fmt = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Compute label-box widths so they stay on the LEFT of the swing,
    // clamped to viewport.
    const LABEL_W = 130;
    const LABEL_H = 18;
    const labelXBase = Math.max(8, x0 - LABEL_W - 8);
    return (
      <g key={d.id}>
        {/* Filled bands between adjacent levels (subtle ~10% alpha). */}
        {rows.slice(0, -1).map((r, i) => {
          const next = rows[i + 1];
          const yTop = Math.min(r.y, next.y);
          const yH = Math.abs(next.y - r.y);
          return (
            <rect key={`band-${r.lvl}`}
                  x={x0} y={yTop} width={xFar - x0} height={yH}
                  fill={r.fill} fillOpacity={0.1} />
          );
        })}
        {/* Horizontal level lines + price/ratio labels on the LEFT. */}
        {rows.map((r) => (
          <g key={`lvl-${r.lvl}`}>
            <line x1={x0} y1={r.y} x2={xFar} y2={r.y}
                  stroke={r.color} strokeWidth={r.bold ? 1.5 : 1}
                  strokeOpacity={r.bold ? 1 : 0.85} />
            <text x={labelXBase + LABEL_W - 8} y={r.y + 4}
                  fill={r.color}
                  style={{ fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace", fontSize: "12px", fontWeight: r.bold ? 700 : 500 }}
                  textAnchor="end">
              {`${r.lvl} (${fmt(r.price)})`}
            </text>
          </g>
        ))}
        {/* Subtle dashed connector between the two anchors. */}
        <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
              stroke="#999" strokeWidth={1} strokeDasharray="5 4" strokeOpacity={0.6} />
        {/* Hollow anchor circles at both ends. */}
        <circle cx={pa.x} cy={pa.y} r={6} fill="none" stroke="#2962ff" strokeWidth={2} />
        <circle cx={pa.x} cy={pa.y} r={3} fill="#ffffff" />
        <circle cx={pb.x} cy={pb.y} r={6} fill="none" stroke="#2962ff" strokeWidth={2} />
        <circle cx={pb.x} cy={pb.y} r={3} fill="#ffffff" />
      </g>
    );
  };

  const renderPreview = () => {
    if (!activeTool || !pending || !hover) return null;
    const pa = { x: pending.x, y: pending.y };
    const pb = { x: hover.x, y: hover.y };
    if (activeTool === "trendline") {
      return <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                   stroke={TOOL_COLORS.trendline} strokeWidth={1.5} strokeDasharray="3 3" />;
    }
    if (activeTool === "ruler") {
      return renderRuler({ id: "preview", a: pending, b: hover });
    }
    if (activeTool === "fib") {
      return renderFib({ id: "preview", a: pending, b: hover });
    }
    return null;
  };

  // Active tool → capture pointer events. Otherwise none so chart panning works.
  const pointer = activeTool ? "auto" : "none";

  return (
    <svg
      ref={ovRef}
      data-testid="chart-drawing-overlay"
      className="absolute inset-0 select-none"
      style={{
        width: "100%",
        height,
        pointerEvents: pointer,
        cursor: activeTool ? "crosshair" : "default",
        // lightweight-charts canvases create their own stacking context;
        // we need a higher z-index so our overlay receives the events.
        zIndex: 5,
      }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {drawings.map((d) => {
        if (d.kind === "trendline") return renderTrendline(d);
        if (d.kind === "ruler") return renderRuler(d);
        if (d.kind === "fib") return renderFib(d);
        return null;
      })}
      {renderPreview()}
    </svg>
  );
}
